import { Button, Icon, Typography } from '@equinor/eds-core-react'
import { Property } from 'csstype'
import styled, { css } from 'styled-components'

export const baseFont = css`
    font-family: Equinor;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
`

export const baseLinkStyles = css`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        background: #eaeaea;
    }

    @media (max-width: 1024px) {
        font-size: 10px;
        flex-direction: row;
    }
`

/** Height of the fixed bottom navigation below 1024px, including the iOS home-indicator inset. */
export const mobileNavHeight = 'calc(60px + env(safe-area-inset-bottom))'

/** Bottom offset of the floating action bar (add sensor / sort / filter) so it clears the navigation. */
export const mobileActionBarBottom = `calc(${mobileNavHeight} + 8px)`

/** Bottom offset for sheets that must open above the floating action bar. */
export const mobileSheetBottom = `calc(${mobileActionBarBottom} + 58px + 8px)`

const ContainerBase = styled.div<{
    $alignItems?: Property.AlignItems
    $justifyContent?: Property.JustifyContent
    $gap?: Property.Gap
}>`
    display: flex;
    justify-content: ${(props) => props.$justifyContent ?? 'unset'};
    gap: ${(props) => props.$gap};
`

export const HContainer = styled(ContainerBase)<{ $alignItems?: Property.AlignItems }>`
    flex-direction: row;
    align-items: ${(props) => props.$alignItems ?? ('center' as Property.AlignItems)};
`

export const VContainer = styled(ContainerBase)<{ $alignItems?: Property.AlignItems }>`
    flex-direction: column;
    align-items: ${(props) => props.$alignItems ?? ('normal' as Property.AlignItems)};
`

/** `DialogActionsContainer` is intended to be used inside the `<Dialog.Actions>`. */
export const DialogActionsContainer = styled(HContainer)`
    gap: 10px;
`

export const Grid = styled.div<{
    $templateColumns?: Property.GridTemplateColumns
    $templateRows?: Property.GridTemplateRows
    $gap?: Property.Gap
    $rowGap?: Property.RowGap
}>`
    display: grid;
    grid-template-columns: ${(props) => props.$templateColumns};
    grid-template-rows: ${(props) => props.$templateRows};
    gap: ${(props) => props.$gap};
    row-gap: ${(props) => props.$rowGap};
`

export const EdsStyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`

export const EdsStyledLabel = styled(Typography)`
    color: #6f6f6f;
    @media (max-width: 1024px) {
        color: #1d1d1d;
    }
`

export const ClosedStyledLabel = styled(Typography)`
    color: #6f6f6f;
    ${baseFont}
    @media (max-width: 1024px) {
        color: #1f6369;
    }
`

export const InputStyledLabel = styled(Typography)`
    color: #6f6f6f;
    ${baseFont}
    @media (max-width: 1024px) {
        color: #585858;
    }
`

export const StyledTypography = styled(Typography)`
    gap: 8px;
    text-decoration: none;
    background: none;
    border: none;
    font-family: Equinor;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 1.1px;
    color: #3d3d3d;
    @media (max-width: 1024px) {
        color: white;
    }
`

export const EdsStyledActionLabel = styled(Typography)`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1;
    color: white;
`

export const EdsStyledButtonSecondary = styled(Button)`
    ${baseLinkStyles}
    color: '#1c1c1c';
    border-radius: 4px;

    &:hover {
        color: #1c1c1c;
        background: #f7f7f7;
    }

    @media (max-width: 1024px) {
        background: #243746;
        border: 1px solid transparent;
        border-color: white;
        color: white;

        &:hover {
            background: #243746;
            color: white;
        }
    }
`

export const EdsStyledButton = styled(Button)`
    ${baseLinkStyles}
    background: #007079;
    color: white;
    border-radius: 4px;

    &:hover {
        color: #1c1c1c;
        background: #f7f7f7;
    }

    @media (max-width: 1024px) {
        background: #007079;
        color: white;

        &:hover {
            background: #007079;
            color: white;
        }
    }
`

export const EdsStyledLink = styled(Button)`
    ${baseLinkStyles}
    background: 'none';
    color: '#1c1c1c';
    border-radius: 4px;

    &:hover {
        color: #1c1c1c;
        background: #f7f7f7;
    }

    @media (max-width: 1024px) {
        background: #deedee;
        color: #004f55;
    }
`

export const StyledErrorTypography = styled(Typography)`
    font-family: Equinor;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    flex-shrink: 0;
    min-width: 0;
    flex: 1;
    color: #b30d2f;
    @media (max-width: 1024px) {
        color: #a50827;
    }
`

export const StyledWarningTypography = styled(Typography)`
    font-family: Equinor;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    flex-shrink: 0;
    min-width: 0;
    flex: 1;
    color: #ad6200;
    @media (max-width: 1024px) {
        color: #8a4100;
    }
`

export const SummaryErrorContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-left: 24px;
    margin-right: 16px;
    margin-bottom: 16px;
    line-height: 16px;
    gap: 8px;
    color: #b30d2f;
    align-items: start;
    @media (max-width: 1024px) {
        color: #a50827;
    }
`

export const SummaryWarningContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-left: 24px;
    margin-bottom: 16px;
    gap: 8px;
    color: #ad6200;
    align-items: start;

    @media (max-width: 1024px) {
        color: #8a4100;
    }
`

export const StyledErrorIcon = styled(Icon)`
    title="Error";
    marginTop: 0;
    color: #b30d2f;

    @media (max-width: 1024px) {
        color: #a50827;
    }
`

export type ActiveProps = {
    $isActive?: boolean
}

export const StyledHelpButton = styled(Button)<ActiveProps>`
    background: ${({ $isActive }) => ($isActive ? '#DEEDEE' : 'transparent')};
    @media (max-width: 1024px) {
        background: transparent;
        color: #585858;
        &:hover {
            background: transparent;
            color: #585858;
        }
    }
`

export const StyledHelpIcon = styled(Icon)<ActiveProps>`
    @media (max-width: 1024px) {
        background: transparent;
        color: ${({ $isActive }) => ($isActive ? '#1F6369' : '#585858')};

        &:hover {
            background: transparent;
            color: ${({ $isActive }) => ($isActive ? '#1F6369' : '#585858')};
        }
    }
`
